import React from "react";

import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import Badge from "@atlaskit/badge";
import Button from "@atlaskit/button-next/new";
import Heading from "@atlaskit/heading";
import { Inline, Stack, Text, xcss } from "@atlaskit/primitives-next";
import Skeleton from "@atlaskit/skeleton";
import Spinner from "@atlaskit/spinner";
import { token } from "@atlaskit/tokens";

import { GenericErrorDisplay } from "@/components/fastshift/src/common/displays";
import { MigrationTicketsCard } from "@/components/fastshift/src/common/migration-tickets-card";
import {
    useFastshiftSelector,
    RESERVED_ORG_IDS
} from "@/components/fastshift/src/state";
import { FASTSHIFT_RELATED_TICKET_ISSUE_TYPE } from "@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types";

import { MultiColorLine } from "../../assets";
import { useDashboardUIAnalytics } from "../hooks/use-dashboard-ui-analytics";
import { useMigrationTicketsListSectionAnalytics } from "../hooks/use-migration-tickets-list-section-analytics";
import { useTopMigrationTickets } from "../hooks/use-top-migration-tickets";

import { messages } from "./messages";

interface MigrationTicketsListProps {
    migrationTicketsUrl: string;
}

const wrapperStyles = xcss({
    border: `1px solid ${token("color.border")}`,
    borderRadius: "4px"
});

const viewAllStyles = xcss({
    borderBlockStart: `1px solid ${token("color.border")}`,
    padding: "space.100"
});

const abnormalStateWrapperStyles = xcss({
    borderRadius: "8px",
    border: `1px solid ${token("color.border")}`,
    padding: "space.200"
});

const iconContainerStyles = xcss({
    width: "48px",
    height: "48px"
});

export const MigrationTicketsList: React.FC<MigrationTicketsListProps> = ({
    migrationTicketsUrl
}) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const selectedDomainOrg = useFastshiftSelector(
        (state) => state.selectedDomainOrg
    );

    const { pending: customerDomainPending, data: customerDomainData } =
        useFastshiftSelector((state) => state.customerDomain);

    const {
        pending: topSupportRequestsPending,
        error: topSupportRequestsError,
        supportTickets,
        total,
        handleRefresh
    } = useTopMigrationTickets();

    const descriptionMessageMapping = (orgId: string) => {
        switch (orgId) {
            case RESERVED_ORG_IDS.NO_ORG:
                return messages.migrationTicketsTableDescriptionWithNoOrg;
            case RESERVED_ORG_IDS.OTHER_ORGS:
                return messages.migrationTicketsTableDescriptionAsNotAnAdmin;
            default:
                return messages.migrationTicketsTableDescriptionWithAnOrg;
        }
    };

    const numOfRemainingTickets = total - supportTickets.length;
    const isEmpty = supportTickets.length === 0;
    const isError = !!topSupportRequestsError;

    useMigrationTicketsListSectionAnalytics();

    const {
        sendMigrationTicketsListViewAllButtonClickUIEvent,
        sendMigrationTicketsTopSectionOpenSupportTicketLinkClickUIEvent
    } = useDashboardUIAnalytics();

    return (
        <Stack space="space.100">
            <Heading size="small">
                <FormattedMessage {...messages.migrationTicketsTableHeading} />
            </Heading>
            {customerDomainPending ? (
                <Skeleton height="20px" width="300px" />
            ) : (
                <Text>
                    <FormattedMessage
                        {...descriptionMessageMapping(
                            selectedDomainOrg?.orgId || ""
                        )}
                    />
                </Text>
            )}
            {customerDomainPending || topSupportRequestsPending ? (
                <Stack
                    xcss={abnormalStateWrapperStyles}
                    alignBlock="center"
                    alignInline="center"
                >
                    <Spinner />
                </Stack>
            ) : isError ? (
                <Stack
                    xcss={abnormalStateWrapperStyles}
                    alignBlock="center"
                    alignInline="center"
                >
                    <GenericErrorDisplay
                        iconSize="small"
                        customMessage={intl.formatMessage(
                            messages.migrationTicketsListErrorMessage
                        )}
                        onRefresh={() =>
                            customerDomainData &&
                            handleRefresh(customerDomainData.customerDomainId)
                        }
                    />
                </Stack>
            ) : isEmpty ? (
                <Stack
                    xcss={abnormalStateWrapperStyles}
                    alignBlock="center"
                    alignInline="center"
                    space="space.100"
                >
                    <Stack
                        xcss={iconContainerStyles}
                        alignBlock="center"
                        alignInline="center"
                    >
                        <MultiColorLine />
                    </Stack>
                    <Text>
                        <FormattedMessage
                            {...messages.migrationTicketsListEmptyState}
                        />
                    </Text>
                </Stack>
            ) : (
                <Stack xcss={wrapperStyles}>
                    {supportTickets.map((ticket, index) => (
                        <MigrationTicketsCard
                            issueType={FASTSHIFT_RELATED_TICKET_ISSUE_TYPE.PLAN}
                            key={ticket.supportTicketId}
                            {...ticket}
                            status={ticket.status}
                            showBottomBorder={index !== 0}
                            onClickAnalyticsEvent={
                                sendMigrationTicketsTopSectionOpenSupportTicketLinkClickUIEvent
                            }
                        />
                    ))}
                    <Inline alignBlock="center" xcss={viewAllStyles}>
                        <Button
                            appearance="subtle"
                            onClick={() => {
                                sendMigrationTicketsListViewAllButtonClickUIEvent();
                                navigate(migrationTicketsUrl);
                            }}
                        >
                            <Inline alignBlock="center" space="space.075">
                                <Text>
                                    <FormattedMessage
                                        {...messages.migrationTicketsListViewAll}
                                    />
                                </Text>
                                {numOfRemainingTickets > 0 && (
                                    <Badge>+{numOfRemainingTickets}</Badge>
                                )}
                            </Inline>
                        </Button>
                    </Inline>
                </Stack>
            )}
        </Stack>
    );
};
